/**
 * WorkflowChecklist - Step 1 of the work order completion workflow.
 *
 * Displays the snapshotted checklist items for the technician working
 * the job. Each item carries a title, optional details, an optional
 * additional callout, and behavior flags (require_photo, is_required)
 * that come from the parent template at assignment time.
 *
 * Multi-tech collaboration: the parent hook polls the server every
 * 10s so completions by other technicians appear here in near
 * real-time. Each item shows who checked it off and when.
 *
 * UI cues for the new field set:
 *   - Required items get a small "Required" tag in the header row.
 *   - Items with require_photo=true render a camera column. Such an
 *     item also can't be checked off until its photo is attached:
 *     tapping the checkbox while the photo is still missing opens the
 *     camera (on the first unfilled slot) instead of completing the
 *     item — see handleToggleItem.
 *   - details + additional_details render as nested paragraphs
 *     beneath the title when present.
 *
 * The "Continue to Verification" CTA is gated on every item being
 * complete and the current user being allowed to act as primary
 * (either the assigned primary tech OR an elevated role:
 * super_admin / admin / company_admin).
 */
import React from 'react';
import type { ChecklistItem, CapturedPhotoPhases } from '../../../services/checklistService';
import { isItemPhotoSatisfied, nextUnfilledPhotoPhase } from '../../../services/checklistService';
import { Alert, PhotoThumbnail } from '../../atoms';
import { ChecklistItemPhotoActions } from '../../molecules/ChecklistItemPhotoActions';
import { ChecklistAddItemRow } from '../../molecules/ChecklistAddItemRow';
import type { CapturePhase, SlotUploadStatuses } from '../../molecules/ChecklistItemPhotoActions';
import { formatAttribution } from '../../../utils/formatDate';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface WorkflowChecklistProps {
    /** Array of checklist items to display */
    items: ChecklistItem[];
    /** Handler to toggle an item's completion status */
    onToggleItem: (itemId: string) => Promise<void>;
    /** Whether all REQUIRED items are completed (sign-off gate) */
    allCompleted: boolean;
    /**
     * Whether the current user is allowed to advance to verification.
     * True for the assigned primary tech AND for elevated roles
     * (super_admin / admin / company_admin) that can act on the
     * team's behalf. The parent owns the role check and passes the
     * resolved boolean down.
     */
    canActAsPrimary: boolean;
    /**
     * Completion mode for the bottom CTA:
     *   - 'verify'         the assignee is also the on-site contact, so the
     *                      completed checklist advances to the in-app Verify
     *                      step (onContinue → goToStep('verify')).
     *   - 'notify_contact' a separate on-site contact will verify + sign off,
     *                      so the CTA hands off to the parent's pre-submit
     *                      confirmation (onContinue → prompt), where the tech
     *                      chooses whether that contact is emailed.
     * Defaults to 'verify' so existing callers are unaffected.
     */
    mode?: 'verify' | 'notify_contact';
    /** On-site contact's display name — shown in the notify_contact helper copy. */
    onSiteContactName?: string | null;
    /** True while the submit-for-verification request is in flight (notify_contact mode). */
    submitting?: boolean;
    /** Handler for the bottom CTA — advance to verify, or submit for verification. */
    onContinue: () => void;
    /**
     * Optional handler for adding an ad-hoc item to this work order's
     * checklist from the field. Omitted (and the control hidden) when the
     * surface is read-only. Adds to THIS work order only — the source
     * template is untouched.
     */
    onAddItem?: (title: string) => Promise<void>;
    /** Whether the device is currently offline */
    isOffline: boolean;
    /** Error message to display */
    error: string | null;
    /**
     * Optional handler invoked when the user taps a camera CTA on an
     * item whose photoMode is 'photo' or 'before_after'. Phase is
     * undefined for single-photo items and 'before' / 'after' for
     * before_after items so the parent's capture modal can scope its
     * upload accordingly.
     */
    onCapturePhotoForItem?: (itemId: string, phase?: CapturePhase) => void;
    /**
     * Optional per-item flags tracking which slots the user has
     * captured a photo for this session. Lets the "Done" badge flip
     * optimistically before the parent's refetch lands. Map keyed by
     * item.id with `{ single?, before?, after? }` values.
     */
    capturedPhotosByItem?: Map<string, CapturedPhotoPhases>;
    /**
     * Per-item background-upload state, keyed by item id. Lets each camera
     * slot say "Sending" / "Failed" instead of claiming "Done" for a photo
     * that hasn't reached the server yet.
     */
    uploadStatusByItem?: Map<string, SlotUploadStatuses>;
    /**
     * Optional handler invoked when the user taps the expand icon on
     * a captured photo thumbnail. Parent owns the lightbox so that
     * stacked overlays (e.g. on top of WorkOrderWorkflow's z-50) live
     * at the same level as the workflow itself.
     */
    onExpandPhoto?: (photoUrl: string, meta: { caption: string | null; area: string | null }) => void;
    /**
     * Optional handler fired when a non-primary tech taps the
     * "Alert The Primary Tech" CTA shown after every item has been
     * checked off. Parent owns the actual delivery (in-app
     * notification, push, SMS, etc.) — this component only renders
     * the affordance and swaps to a "sent" confirmation once the
     * `alertSent` flag flips true.
     */
    onAlertPrimary?: () => void;
    /** Primary tech's display name — surfaced in the CTA confirmation copy */
    primaryContactName?: string | null;
    /** Whether the alert request has already been fired this session */
    alertSent?: boolean;
    /** True while the alert request is in flight (disables the button) */
    alertSending?: boolean;
    /** Optional outer className merged onto the root element. */
    className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

const WorkflowChecklist: React.FC<WorkflowChecklistProps> = ({
    items,
    onToggleItem,
    allCompleted,
    canActAsPrimary,
    mode = 'verify',
    onSiteContactName,
    submitting,
    onContinue,
    onAddItem,
    isOffline,
    error,
    onCapturePhotoForItem,
    capturedPhotosByItem,
    uploadStatusByItem,
    onExpandPhoto,
    onAlertPrimary,
    primaryContactName,
    alertSent,
    alertSending,
    className,
}) => {
    /* Sort items by sortOrder once on each render — items array is small */
    const sortedItems = [...items].sort((a, b) => a.sortOrder - b.sortOrder);

    /* Row id whose required-photo hint is currently shown. Set when a tech
     * taps the checkbox of a require_photo item that has no camera wired
     * (companyId unknown, so there's nothing to open); cleared on a
     * successful toggle. */
    const [photoHintItemId, setPhotoHintItemId] = React.useState<string | null>(null);

    /* Calculate completion progress for the top-of-screen header */
    const completedCount = items.filter((i) => i.isCompleted).length;
    const totalCount = items.length;
    const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

    /* Required-item progress: how many *required* items are still unchecked.
     * Surfaced separately so techs know when sign-off is unblocked even if
     * optional items remain. */
    const requiredItems = items.filter((i) => i.isRequired);
    const requiredRemaining = requiredItems.filter((i) => !i.isCompleted).length;

    /* Photo-aware completion accounting for the footer.
     *
     * "Needs action" = an item that's either unchecked OR checked but
     * still missing a required photo (a legacy / mid-capture state). This
     * is the SAME union the Submit gate (allCompleted, owned by the
     * parent) uses, so the footer count and the Submit button can never
     * disagree — the count hits 0 exactly when the button appears.
     *
     * `photosOutstanding` is the subset that's blocked purely on a photo
     * (box already ticked) so we can show a clearer "N photos needed"
     * message when that's all that's left. */
    const itemsNeedingAction = items.filter(
        (i) => !i.isCompleted || (i.isRequired && !isItemPhotoSatisfied(i, capturedPhotosByItem?.get(i.id))),
    ).length;
    const photosOutstanding = items.filter(
        (i) => i.isCompleted && i.isRequired && !isItemPhotoSatisfied(i, capturedPhotosByItem?.get(i.id)),
    ).length;

    /* Photo-gate the checkbox.
     *
     * An item flagged require_photo can't be checked off until its
     * photo requirement is met. When a tech taps the checkbox while a
     * required photo is still missing we DON'T mark it complete —
     * instead we open the camera on the first unfilled slot so the
     * photo gets captured first ("you must add the image first"). Once
     * the photo lands (the thumbnail strip appears) the next tap checks
     * the item off normally.
     *
     * The gate only fires on the way UP (unchecked → checked); a tech
     * can always uncheck a completed item. `photoMissing` is computed
     * per-row in the render below and handed in so we don't recompute
     * the satisfied-check here.
     *
     * The block is ALWAYS enforced when a photo is missing. If a capture
     * handler is wired we open the camera; if not (companyId unknown, so
     * there's no camera to open) we surface an inline hint instead of
     * silently checking the box — the required-photo rule can never be
     * bypassed, even in that degenerate config. */
    const handleToggleItem = (item: ChecklistItem, photoMissing: boolean) => {
        if (photoMissing) {
            if (onCapturePhotoForItem) {
                // Open the camera on the first unfilled slot (before → after).
                onCapturePhotoForItem(
                    item.id,
                    nextUnfilledPhotoPhase(item, capturedPhotosByItem?.get(item.id)),
                );
            } else {
                // No camera to open — explain why the box won't check.
                setPhotoHintItemId(item.id);
            }
            return;
        }
        // Photo satisfied (or not required) — clear any stale hint and toggle.
        setPhotoHintItemId(null);
        void onToggleItem(item.id);
    };

    return (
        <div className={['workflow-checklist', 'flex flex-col h-full', className ?? ''].join(' ')} data-ref="organism-workflow-checklist">
            {/* --- Progress header --- */}
            <div className="workflow-checklist__header px-16px pt-16px pb-12px">
                {/* Progress bar showing completion percentage */}
                <div className="flex items-center justify-between mb-8px">
                    <span className="text-14px font-sans font-semibold text-brand-text">
                        {completedCount} of {totalCount} completed
                    </span>
                    <span className="text-14px font-sans font-medium text-brand-text-muted">
                        {progressPercent}%
                    </span>
                </div>
                <div className="w-full h-8px bg-gray-200 rounded-full overflow-hidden">
                    <div
                        className="h-full bg-brand-primary rounded-full transition-all duration-300 ease-out"
                        style={{ width: `${progressPercent}%` }}
                    />
                </div>

                {/* Required-items hint — only shown when relevant */}
                {requiredItems.length > 0 && requiredRemaining > 0 && (
                    <p className="text-12px font-sans text-brand-text-muted mt-4px">
                        {requiredRemaining} required item{requiredRemaining === 1 ? '' : 's'} remaining
                    </p>
                )}

                {/* Offline indicator */}
                {isOffline && (
                    <div className="flex items-center gap-6px mt-8px px-10px py-6px bg-amber-50 border border-amber-200 rounded-md">
                        {/* Offline cloud icon */}
                        <svg className="h-14px w-14px text-amber-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9.75v6.75m0 0-3-3m3 3 3-3m-8.25 6a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.233-2.33 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18 19.5H6.75Z" />
                        </svg>
                        <span className="text-12px font-sans text-amber-700">
                            Offline — changes will sync when you reconnect
                        </span>
                    </div>
                )}

                {/* Error message */}
                {error && (
                    <Alert variant="error" size="sm" icon className="mt-8px">{error}</Alert>
                )}
            </div>

            {/* --- Scrollable checklist items --- */}
            <div className="workflow-checklist__body flex-1 overflow-y-auto px-16px pb-16px">
                <ul className="flex flex-col gap-6px">
                    {sortedItems.map((item) => {
                        /* True when this row is blocked from being checked off
                         * because a required photo hasn't been attached yet.
                         * Drives both the toggle redirect (open the camera) and
                         * the row's aria-label so the affordance is announced. */
                        const photoMissing =
                            !item.isCompleted &&
                            item.photoMode !== 'none' &&
                            item.photoRequired &&
                            !isItemPhotoSatisfied(item, capturedPhotosByItem?.get(item.id));

                        return (
                        <li key={item.id}>
                            {/* Outer container is a div, NOT a button — the row contains
                             *  several interactive elements (toggle, camera CTA, photo
                             *  expand buttons) and nesting buttons is invalid HTML. The
                             *  row stacks vertically: a horizontal row holding the
                             *  toggle + camera CTA on top, then an optional photo
                             *  thumbnail strip beneath when the item has captured
                             *  photos. Each interactive element is its own <button>
                             *  sibling so clicks never bubble across them. */}
                            <div
                                className={[
                                    'w-full flex flex-col rounded-lg border transition-all duration-150 overflow-hidden',
                                    item.isCompleted
                                        ? 'bg-green-50 border-green-200'
                                        : 'bg-white border-gray-200',
                                ].join(' ')}
                            >
                                <div className="flex items-stretch gap-8px">
                                <button
                                    type="button"
                                    onClick={() => handleToggleItem(item, photoMissing)}
                                    className={[
                                        'flex-1 flex items-start gap-12px p-12px rounded-lg min-h-44px text-left',
                                        item.isCompleted
                                            ? ''
                                            : 'hover:bg-brand-primary/5 active:bg-brand-primary/10',
                                    ].join(' ')}
                                    aria-label={
                                        photoMissing
                                            ? `Add required photo to complete: ${item.title}`
                                            : `${item.isCompleted ? 'Uncheck' : 'Check'}: ${item.title}`
                                    }
                                >
                                {/* Checkbox indicator */}
                                <div className={[
                                    'flex-shrink-0 w-24px h-24px rounded-md border-2 flex items-center justify-center mt-1px transition-colors',
                                    item.isCompleted
                                        ? 'bg-green-600 border-green-600'
                                        : 'bg-white border-gray-300',
                                ].join(' ')}>
                                    {item.isCompleted && (
                                        <svg className="w-14px h-14px text-white" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" aria-hidden="true">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                                        </svg>
                                    )}
                                </div>

                                {/* Item title, details, and badges */}
                                <div className="flex-1 min-w-0">
                                    {/* Required badge sits above the title, left-aligned,
                                     *  so the tag and the text share the same column instead
                                     *  of competing for space on the right edge of the row. */}
                                    {item.isRequired && (
                                        <span className="inline-block text-10px font-sans font-medium text-amber-700 bg-amber-100 px-6px py-2px rounded uppercase tracking-wide mb-4px">
                                            Required
                                        </span>
                                    )}
                                    <span className={[
                                        'block text-15px font-sans font-medium leading-snug',
                                        item.isCompleted ? 'text-green-800 line-through' : 'text-brand-text',
                                    ].join(' ')}>
                                        {item.title}
                                    </span>

                                    {/* Primary description text — auto-grow textarea on the CMS side */}
                                    {item.details && (
                                        <p className={[
                                            'text-13px font-sans leading-snug mt-4px whitespace-pre-line',
                                            item.isCompleted ? 'text-green-700/80' : 'text-brand-text-muted',
                                        ].join(' ')}>
                                            {item.details}
                                        </p>
                                    )}

                                    {/* Optional callout — visually distinct so it doesn't blend in */}
                                    {item.additionalDetails && (
                                        <p className={[
                                            'text-12px font-sans leading-snug mt-4px italic px-8px py-4px rounded',
                                            item.isCompleted ? 'bg-green-50/40 text-green-700/80' : 'bg-amber-50 text-amber-800 border border-amber-100',
                                        ].join(' ')}>
                                            {item.additionalDetails}
                                        </p>
                                    )}

                                    {/* Completion attribution — full date+time format
                                     *  per spec: "DAY, MM/DD/YYYY HH:MM AM/PM TZ".
                                     *  Persisted server-side via the toggle endpoint
                                     *  which records completed_by_id + completed_at. */}
                                    {item.isCompleted && item.completedByName && (
                                        <span className="text-11px font-sans text-green-600 mt-4px block">
                                            {item.completedByName} &middot;{' '}
                                            {item.completedAt ? formatAttribution(item.completedAt) : 'just now'}
                                        </span>
                                    )}

                                    {/* Required-photo hint — shown only when a tech taps
                                     *  the checkbox but no camera is available to capture
                                     *  the photo (companyId unknown). Explains why the box
                                     *  won't check so the tech isn't left stuck. */}
                                    {photoHintItemId === item.id && (
                                        <span className="text-11px font-sans text-red-600 mt-4px block">
                                            A required photo must be added before this can be checked.
                                        </span>
                                    )}
                                </div>
                                </button>

                                {/* Camera CTA column — shared molecule branches
                                 *  on item.photoMode so this single component
                                 *  handles single-photo, before/after, and
                                 *  the no-op 'none' case in one place. */}
                                {onCapturePhotoForItem && (
                                    <ChecklistItemPhotoActions
                                        item={item}
                                        onCapture={onCapturePhotoForItem}
                                        captured={capturedPhotosByItem?.get(item.id)}
                                        uploadStatus={uploadStatusByItem?.get(item.id)}
                                    />
                                )}
                                </div>

                                {/* Captured photos strip — sibling row beneath the
                                 *  toggle + camera CTA. Each tile delegates to the
                                 *  PhotoThumbnail atom which owns the expand
                                 *  overlay; this row just lays them out. The strip
                                 *  only renders when at least one photo is attached
                                 *  so the row card stays compact for empty items.
                                 *  Items in photoMode='before_after' get a small
                                 *  BEFORE/AFTER tag overlaid on each tile so the
                                 *  user can read which slot a thumbnail belongs to
                                 *  without opening it. */}
                                {item.photos && item.photos.length > 0 && (
                                    <div className={[
                                        'flex flex-wrap gap-6px px-12px py-8px border-t',
                                        item.isCompleted ? 'border-green-200/70 bg-green-50/40' : 'border-gray-100 bg-gray-50/50',
                                    ].join(' ')}>
                                        {item.photos.map((photo) => (
                                            <div key={photo.id} className="relative">
                                                <PhotoThumbnail
                                                    src={photo.url}
                                                    alt={photo.caption ?? 'Captured photo'}
                                                    size="md"
                                                    onExpand={
                                                        onExpandPhoto
                                                            ? () =>
                                                                  onExpandPhoto(photo.url, {
                                                                      caption: photo.caption,
                                                                      area: photo.area,
                                                                  })
                                                            : undefined
                                                    }
                                                />
                                                {item.photoMode === 'before_after' && photo.photoPhase && (
                                                    <span className="absolute top-2px left-2px text-9px font-bold uppercase tracking-wide bg-black/70 text-white px-4px py-1px rounded">
                                                        {photo.photoPhase}
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </li>
                        );
                    })}
                </ul>

                {/* Empty state when no items have been assigned */}
                {sortedItems.length === 0 && (
                    <p className="text-center text-14px font-sans text-brand-text-muted py-32px">
                        No checklist items have been assigned to this work order.
                    </p>
                )}

                {/* --- Add an item on the fly ---
                    The week's work rarely matches the template line-for-line;
                    this is where the crew records what they actually did
                    ("Rooms 201-220") before the customer signs. Adds to THIS
                    work order only, as an optional item, so a late addition
                    never blocks the sign-off it exists to support. */}
                {onAddItem && (
                    <ChecklistAddItemRow
                        block="workflow-checklist"
                        placeholder="e.g. Rooms 201-220"
                        onAddItem={onAddItem}
                    />
                )}
            </div>

            {/* --- Bottom action bar --- */}
            {/* Shows "Continue to Verification" when all items are done
             *  and the user can act as primary (assigned primary tech OR
             *  an elevated role). Non-primary techs without that role
             *  see the "Alert The Primary Tech" CTA instead. */}
            <div className="workflow-checklist__footer px-16px pb-16px pt-8px border-t border-gray-100">
                {mode === 'notify_contact' ? (
                    /* Separate on-site contact handles verification. Completing
                     * the checklist submits the work order for that contact to
                     * verify + sign off (they're notified server-side). */
                    allCompleted ? (
                        <div className="flex flex-col gap-8px">
                            <button
                                type="button"
                                onClick={onContinue}
                                disabled={submitting}
                                className="w-full inline-flex items-center justify-center gap-8px px-20px py-14px text-16px font-sans font-semibold text-white bg-brand-primary hover:bg-brand-primary-dark rounded-lg min-h-48px transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {submitting ? 'Submitting…' : 'Submit'}
                                {!submitting && (
                                    <svg className="w-18px h-18px" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                                    </svg>
                                )}
                            </button>
                            {/* Sets the expectation that Submit opens a
                              * confirmation first — the contact is only emailed
                              * if the tech says so there. */}
                            <p className="text-center text-13px font-sans text-brand-text-muted">
                                {onSiteContactName
                                    ? `You'll confirm whether to email ${onSiteContactName} to verify and sign off.`
                                    : "You'll confirm who gets emailed to verify and sign off."}
                            </p>
                        </div>
                    ) : (
                        <p className="text-center text-14px font-sans text-brand-text-muted py-4px">
                            {/* Photo-aware remaining count — never reads "0 remaining"
                             *  while a required photo is still outstanding, so it can't
                             *  disagree with the Submit gate above. */}
                            {photosOutstanding > 0 && photosOutstanding === itemsNeedingAction
                                ? `${photosOutstanding} required photo${photosOutstanding === 1 ? '' : 's'} needed`
                                : `${itemsNeedingAction} item${itemsNeedingAction === 1 ? '' : 's'} remaining`}
                        </p>
                    )
                ) : allCompleted && canActAsPrimary ? (
                    <button
                        type="button"
                        onClick={onContinue}
                        className="w-full inline-flex items-center justify-center gap-8px px-20px py-14px text-16px font-sans font-semibold text-white bg-brand-primary hover:bg-brand-primary-dark rounded-lg min-h-48px transition-colors shadow-sm"
                    >
                        Continue to Verification
                        {/* Arrow right icon */}
                        <svg className="w-18px h-18px" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                        </svg>
                    </button>
                ) : allCompleted && !canActAsPrimary ? (
                    /* Non-primary techs are blocked from advancing the workflow,
                     * so we still surface the "primary will verify" reminder.
                     * When a parent has supplied an onAlertPrimary handler we
                     * also render a CTA so the tech can proactively ping the
                     * primary tech to come close out the job. After the alert
                     * has been fired (alertSent === true) the button collapses
                     * into a "Alert sent" confirmation so repeated taps don't
                     * spam the primary tech. */
                    <div className="flex flex-col gap-8px">
                        <div className="flex items-center gap-8px px-12px py-10px bg-blue-50 border border-blue-200 rounded-lg">
                            <svg className="h-18px w-18px text-blue-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
                            </svg>
                            <span className="text-14px font-sans text-blue-700">
                                All items complete. The primary technician will verify and sign off.
                            </span>
                        </div>

                        {onAlertPrimary && (
                            alertSent ? (
                                /* Confirmation state — the alert has been delivered.
                                 * Visually mirrors the success styling used on
                                 * completed checklist items so the tech gets
                                 * unambiguous positive feedback. */
                                <div className="flex items-center justify-center gap-8px px-12px py-12px bg-green-50 border border-green-200 rounded-lg">
                                    <svg className="h-18px w-18px text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                                    </svg>
                                    <span className="text-14px font-sans font-medium text-green-700">
                                        {primaryContactName
                                            ? `${primaryContactName} has been alerted`
                                            : 'Primary tech has been alerted'}
                                    </span>
                                </div>
                            ) : (
                                /* Action CTA — visually dominant so a tech who
                                 * just finished their last item sees an obvious
                                 * next step. Mirrors the styling of the primary
                                 * "Continue to Verification" button above. */
                                <button
                                    type="button"
                                    onClick={onAlertPrimary}
                                    disabled={alertSending}
                                    className="w-full inline-flex items-center justify-center gap-8px px-20px py-14px text-16px font-sans font-semibold text-white bg-brand-primary hover:bg-brand-primary-dark rounded-lg min-h-48px transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                                    aria-label={primaryContactName ? `Alert ${primaryContactName}` : 'Alert the primary tech'}
                                >
                                    {/* Bell icon — telegraphs "notification" */}
                                    <svg className="w-18px h-18px" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0" />
                                    </svg>
                                    {alertSending ? 'Sending…' : 'Alert The Primary Tech'}
                                </button>
                            )
                        )}
                    </div>
                ) : (
                    /* Progress encouragement when items are still pending.
                     * Photo-aware count (mirrors the notify_contact branch above)
                     * so the text never disagrees with the Continue gate. */
                    <p className="text-center text-14px font-sans text-brand-text-muted py-4px">
                        {photosOutstanding > 0 && photosOutstanding === itemsNeedingAction
                            ? `${photosOutstanding} required photo${photosOutstanding === 1 ? '' : 's'} needed`
                            : `${itemsNeedingAction} item${itemsNeedingAction === 1 ? '' : 's'} remaining`}
                    </p>
                )}
            </div>
        </div>
    );
};

export default WorkflowChecklist;
